import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Eye, Search, Package, X, ChevronDown, Copy, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  code: "", name: "", drawing_number: "", sku: "", client_id: "",
  sale_price: 0, weight: 0, height: 0, width: 0, length: 0, volume: 0,
  contract_total: 0,
  stages: []
};

export default function ProductsPage() {
  const { can } = useAuth();
  const isAdmin = can("produtos", "create");
  const canEdit = can("produtos", "edit");
  const canDelete = can("produtos", "delete");
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [defaultStages, setDefaultStages] = useState([]);
  const [configUnits, setConfigUnits] = useState([]);
  const [matSearch, setMatSearch] = useState("");
  const [matSearchStageIdx, setMatSearchStageIdx] = useState(null);
  const [matQty, setMatQty] = useState(1);
  const [expandedStages, setExpandedStages] = useState({});
  const [clients, setClients] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(data);
    } catch { toast.error("Erro ao carregar produtos"); }
    finally { setLoading(false); }
  }, []);

  const fetchMaterials = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/materials`, { withCredentials: true }); setMaterials(data); } catch (err) { console.error("fetchMaterials:", err); }
  }, []);

  useEffect(() => {
    fetchProducts(); fetchMaterials();
    axios.get(`${API}/settings/default_stages`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setDefaultStages(data.items); }).catch(() => {});
    axios.get(`${API}/settings/units`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setConfigUnits(data.items); }).catch(() => {});
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(({ data }) => setClients(data)).catch(() => {});
  }, [fetchProducts, fetchMaterials]);

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setForm({
      code: product.code || "",
      name: product.name || "",
      drawing_number: product.drawing_number || "",
      sku: product.sku || "",
      client_id: product.client_id || "",
      sale_price: product.sale_price || 0,
      weight: product.weight || 0,
      height: product.height || 0,
      width: product.width || 0,
      length: product.length || 0,
      volume: product.volume || 0,
      contract_total: product.contract_total || 0,
      stages: (product.stages || []).map((s) => {
        const matched = defaultStages.find((ds) => ds.value === s.name);
        return {
          name: matched ? matched.label : s.name,
          sub_stages: (s.sub_stages || []).map((ss) => ({ name: ss.name, quantity: ss.quantity || 1, unit: ss.unit || "unidade" })),
          materials: (s.materials || []).map((m) => ({ material_id: m.material_id, planned_quantity: m.planned_quantity }))
        };
      })
    });
    setEditOpen(true);
  };

  const openDetail = (product) => {
    setSelectedProduct(product);
    setExpandedStages({});
    setDetailOpen(true);
  };

  const handleProductUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProduct) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/products/${selectedProduct.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setSelectedProduct((prev) => ({ ...prev, files: [...(prev.files || []), data] }));
      toast.success("Desenho anexado");
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar arquivo"); }
    finally { setUploadingFile(false); e.target.value = ""; }
  };

  const downloadProductFile = async (fileRef) => {
    try {
      const response = await axios.get(`${API}/products/${selectedProduct.id}/files/${fileRef.id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a"); a.href = url; a.download = fileRef.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar arquivo"); }
  };

  const handleDeleteProductFile = async (fileId) => {
    if (!selectedProduct) return;
    try {
      await axios.delete(`${API}/products/${selectedProduct.id}/files/${fileId}`, { withCredentials: true });
      setSelectedProduct((prev) => ({ ...prev, files: (prev.files || []).filter((f) => f.id !== fileId) }));
      toast.success("Desenho removido");
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover arquivo"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome do produto e obrigatorio"); return; }
    try {
      const payload = {
        ...form,
        stages: form.stages.filter((s) => s.name.trim()).map((s) => ({
          name: s.name.trim(),
          sub_stages: (s.sub_stages || []).filter((ss) => ss.name.trim()),
          materials: (s.materials || []).filter((m) => m.material_id && m.planned_quantity > 0)
        }))
      };
      await axios.post(`${API}/products`, payload, { withCredentials: true });
      toast.success("Produto cadastrado com sucesso");
      setCreateOpen(false);
      setForm(emptyForm);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao cadastrar produto"); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    try {
      const payload = {
        ...form,
        stages: form.stages.filter((s) => s.name.trim()).map((s) => ({
          name: s.name.trim(),
          sub_stages: (s.sub_stages || []).filter((ss) => ss.name.trim()),
          materials: (s.materials || []).filter((m) => m.material_id && m.planned_quantity > 0)
        }))
      };
      await axios.put(`${API}/products/${selectedProduct.id}`, payload, { withCredentials: true });
      toast.success("Produto atualizado");
      setEditOpen(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar produto"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusao do produto?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { withCredentials: true });
      toast.success("Produto removido");
      if (selectedProduct?.id === id) { setDetailOpen(false); setSelectedProduct(null); }
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir"); }
  };

  const handleDuplicate = (product) => {
    setForm({
      code: product.code ? product.code + " (copia)" : "",
      name: product.name + " (copia)",
      drawing_number: product.drawing_number || "",
      sku: "",
      client_id: product.client_id || "",
      sale_price: product.sale_price || 0,
      weight: product.weight || 0,
      height: product.height || 0,
      width: product.width || 0,
      length: product.length || 0,
      volume: product.volume || 0,
      contract_total: product.contract_total || 0,
      stages: (product.stages || []).map((s) => {
        const matched = defaultStages.find((ds) => ds.value === s.name);
        return {
          name: matched ? matched.label : s.name,
          sub_stages: (s.sub_stages || []).map((ss) => ({ name: ss.name, quantity: ss.quantity || 1, unit: ss.unit || "unidade" })),
          materials: (s.materials || []).map((m) => ({ material_id: m.material_id, planned_quantity: m.planned_quantity }))
        };
      })
    });
    setDetailOpen(false);
    setCreateOpen(true);
    toast.info("Produto duplicado - altere os dados e salve");
  };
  const addStage = () => setForm({ ...form, stages: [...form.stages, { name: "", sub_stages: [], materials: [] }] });
  const removeStage = (idx) => setForm({ ...form, stages: form.stages.filter((_, i) => i !== idx) });
  const updateStageName = (idx, name) => {
    const s = [...form.stages]; s[idx] = { ...s[idx], name }; setForm({ ...form, stages: s });
  };

  // Sub-stage management
  const addSubStage = (stageIdx) => {
    const s = [...form.stages];
    s[stageIdx] = { ...s[stageIdx], sub_stages: [...(s[stageIdx].sub_stages || []), { name: "", quantity: 1, unit: "unidade" }] };
    setForm({ ...form, stages: s });
  };
  const removeSubStage = (stageIdx, subIdx) => {
    const s = [...form.stages];
    s[stageIdx] = { ...s[stageIdx], sub_stages: s[stageIdx].sub_stages.filter((_, i) => i !== subIdx) };
    setForm({ ...form, stages: s });
  };
  const updateSubStage = (stageIdx, subIdx, field, value) => {
    const s = [...form.stages]; const subs = [...s[stageIdx].sub_stages];
    subs[subIdx] = { ...subs[subIdx], [field]: value };
    s[stageIdx] = { ...s[stageIdx], sub_stages: subs }; setForm({ ...form, stages: s });
  };

  // Material management in stages
  const addMaterialToStage = (stageIdx, matId, qty) => {
    const mat = materials.find((m) => m.id === matId);
    if (!mat) return;
    const s = [...form.stages];
    const existing = (s[stageIdx].materials || []).find((m) => m.material_id === matId);
    if (existing) { toast.error("Material ja adicionado nesta etapa"); return; }
    s[stageIdx] = { ...s[stageIdx], materials: [...(s[stageIdx].materials || []), { material_id: matId, planned_quantity: qty }] };
    setForm({ ...form, stages: s });
    setMatSearch(""); setMatQty(1); setMatSearchStageIdx(null);
  };
  const removeMaterialFromStage = (stageIdx, matIdx) => {
    const s = [...form.stages];
    s[stageIdx] = { ...s[stageIdx], materials: s[stageIdx].materials.filter((_, i) => i !== matIdx) };
    setForm({ ...form, stages: s });
  };

  const getMaterialInfo = (matId) => materials.find((m) => m.id === matId);

  const filteredMats = matSearch.trim() ? materials.filter((m) => {
    const matchesSearch = (m.code || "").toLowerCase().includes(matSearch.toLowerCase()) ||
      (m.name || "").toLowerCase().includes(matSearch.toLowerCase());
    if (!matchesSearch) return false;
    // Strict filter: only show materials of the product's client
    if (form.client_id) {
      return m.client_id === form.client_id;
    }
    return true;
  }).slice(0, 8) : [];

  const calcVolume = () => {
    if (form.height && form.width && form.length) {
      setForm({ ...form, volume: parseFloat((form.height * form.width * form.length).toFixed(4)) });
    }
  };

  const term = searchTerm.toLowerCase().trim();
  const filtered = term ? products.filter((p) =>
    (p.code || "").toLowerCase().includes(term) || (p.name || "").toLowerCase().includes(term) || (p.sku || "").toLowerCase().includes(term)
  ) : products;

  const toggleStageExpand = (id) => setExpandedStages((prev) => ({ ...prev, [id]: !prev[id] }));

  // Shared form renderer for create/edit
  const renderProductForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Codigo Interno</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex: KH-001" data-testid="product-code-input" />
        </div>
        <div className="space-y-2">
          <Label>Nome do Produto *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ex: Kit Hidraulico Residencial" data-testid="product-name-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Numero do Desenho</Label>
          <Input value={form.drawing_number} onChange={(e) => setForm({ ...form, drawing_number: e.target.value })} placeholder="Ex: DES-2026-001" data-testid="product-drawing-input" />
        </div>
        <div className="space-y-2">
          <Label>Cliente</Label>
          <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="product-client-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SKU (Ordem de Fabricacao)</Label>
          <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Ex: OF-001" data-testid="product-sku-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Preco de Venda (R$)</Label>
          <Input type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value) || 0 })} data-testid="product-price-input" />
        </div>
        <div className="space-y-2">
          <Label>Peso (kg)</Label>
          <Input type="number" step="0.01" min="0" value={form.weight} onChange={(e) => setForm({ ...form, weight: parseFloat(e.target.value) || 0 })} data-testid="product-weight-input" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="space-y-2">
          <Label>Altura (m)</Label>
          <Input type="number" step="0.01" min="0" value={form.height} onChange={(e) => setForm({ ...form, height: parseFloat(e.target.value) || 0 })} onBlur={calcVolume} data-testid="product-height-input" />
        </div>
        <div className="space-y-2">
          <Label>Largura (m)</Label>
          <Input type="number" step="0.01" min="0" value={form.width} onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) || 0 })} onBlur={calcVolume} data-testid="product-width-input" />
        </div>
        <div className="space-y-2">
          <Label>Comprimento (m)</Label>
          <Input type="number" step="0.01" min="0" value={form.length} onChange={(e) => setForm({ ...form, length: parseFloat(e.target.value) || 0 })} onBlur={calcVolume} data-testid="product-length-input" />
        </div>
        <div className="space-y-2">
          <Label>Cubagem (m3)</Label>
          <Input type="number" step="0.0001" min="0" value={form.volume} onChange={(e) => setForm({ ...form, volume: parseFloat(e.target.value) || 0 })} data-testid="product-volume-input" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-2">
          <Label>Total de Kits a Produzir (Contrato)</Label>
          <Input
            type="number"
            step="1"
            min="0"
            value={form.contract_total}
            onChange={(e) => setForm({ ...form, contract_total: parseInt(e.target.value, 10) || 0 })}
            placeholder="Ex: 500 - quantidade total contratada pelo cliente"
            data-testid="product-contract-total-input"
          />
          <p className="text-xs text-muted-foreground">
            Quantidade acordada com o cliente. O sistema abate automaticamente a cada ordem concluida ou expedida.
          </p>
        </div>
      </div>

      {/* Stages Template */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="font-semibold">Etapas do Processo</Label>
          <Button type="button" variant="outline" size="sm" onClick={addStage} data-testid="product-add-stage-btn">
            <Plus className="h-3 w-3 mr-1" /> Etapa
          </Button>
        </div>
        {form.stages.map((stage, sIdx) => (
          <div key={sIdx} className="border rounded-lg p-3 space-y-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground w-5">{sIdx + 1}</span>
              {defaultStages.length > 0 ? (
                <Select value={stage.name} onValueChange={(v) => updateStageName(sIdx, v)}>
                  <SelectTrigger className="flex-1" data-testid={`product-stage-select-${sIdx}`}><SelectValue placeholder="Selecionar etapa" /></SelectTrigger>
                  <SelectContent>
                    {defaultStages.map((ds) => (<SelectItem key={ds.value} value={ds.label}>{ds.label}</SelectItem>))}
                    {stage.name && !defaultStages.some((ds) => ds.label === stage.name) && (
                      <SelectItem key={`custom-${sIdx}`} value={stage.name}>{stage.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <Input className="flex-1" value={stage.name} onChange={(e) => updateStageName(sIdx, e.target.value)} placeholder="Nome da etapa" />
              )}
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeStage(sIdx)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Sub-stages */}
            <div className="pl-7 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">Sub-etapas</span>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => addSubStage(sIdx)}>
                  <Plus className="h-3 w-3 mr-1" /> Sub-etapa
                </Button>
              </div>
              {(stage.sub_stages || []).map((ss, ssIdx) => (
                <div key={ssIdx} className="flex items-center gap-2">
                  <Input className="flex-1" value={ss.name} onChange={(e) => updateSubStage(sIdx, ssIdx, "name", e.target.value)} placeholder="Nome" />
                  <Input className="w-16" type="number" min={1} value={ss.quantity} onChange={(e) => updateSubStage(sIdx, ssIdx, "quantity", parseInt(e.target.value) || 1)} />
                  <Select value={ss.unit} onValueChange={(v) => updateSubStage(sIdx, ssIdx, "unit", v)}>
                    <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(configUnits.length ? configUnits : [{ value: "unidade", label: "Unidade" }, { value: "peca", label: "Peca" }, { value: "metro", label: "Metro" }]).map((u) => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeSubStage(sIdx, ssIdx)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Materials */}
            <div className="pl-7 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">Materiais</span>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setMatSearchStageIdx(matSearchStageIdx === sIdx ? null : sIdx)}>
                  <Plus className="h-3 w-3 mr-1" /> Material
                </Button>
              </div>
              {(stage.materials || []).map((m, mIdx) => {
                const info = getMaterialInfo(m.material_id);
                return (
                  <div key={mIdx} className="flex items-center gap-2 text-sm bg-background rounded px-2 py-1">
                    <span className="flex-1 truncate">{info ? `${info.code ? info.code + " - " : ""}${info.name}` : m.material_id}</span>
                    <span className="text-muted-foreground">{m.planned_quantity} {info?.unit || ""}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeMaterialFromStage(sIdx, mIdx)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
              {matSearchStageIdx === sIdx && (
                <div className="border rounded p-2 bg-background space-y-2">
                  <Input placeholder="Buscar por codigo ou nome..." value={matSearch} onChange={(e) => setMatSearch(e.target.value)} data-testid={`product-mat-search-${sIdx}`} autoFocus />
                  {filteredMats.length > 0 && (
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {filteredMats.map((m) => (
                        <div key={m.id} className="flex items-center justify-between px-2 py-1 hover:bg-muted rounded cursor-pointer text-sm" onClick={() => addMaterialToStage(sIdx, m.id, matQty)}>
                          <span className="truncate">{m.code ? m.code + " - " : ""}{m.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{m.quantity} {m.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Qtd:</Label>
                    <Input type="number" min={0.01} step={0.01} value={matQty} onChange={(e) => setMatQty(parseFloat(e.target.value) || 0.01)} className="w-20 h-8" />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditOpen(false); }}>Cancelar</Button>
        <Button type="submit" data-testid="product-submit-btn">{submitLabel}</Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6 animate-slide-in" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Produtos</h1>
          <p className="text-sm text-muted-foreground mt-1">Catalogo de produtos e templates de producao</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="create-product-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Produto
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por codigo, nome ou SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="product-search-input" />
      </div>

      <Card className="border border-border shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[1080px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Codigo</TableHead>
                <TableHead className="w-[200px]">Nome</TableHead>
                <TableHead className="w-[120px]">Cliente</TableHead>
                <TableHead className="w-[100px]">SKU</TableHead>
                <TableHead className="w-[100px]">Desenho</TableHead>
                <TableHead className="w-[110px]">Anexo</TableHead>
                <TableHead className="w-[90px]">Preco</TableHead>
                <TableHead className="w-[70px]">Peso</TableHead>
                <TableHead className="w-[70px]">Etapas</TableHead>
                <TableHead className="w-[90px]">Materiais</TableHead>
                <TableHead className="w-[180px]">Contrato (Entregue/Total)</TableHead>
                <TableHead className="w-[100px]">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                  {term ? "Nenhum produto encontrado" : "Nenhum produto cadastrado"}
                </TableCell></TableRow>
              ) : (
                filtered.map((p) => {
                  const totalMats = (p.stages || []).reduce((acc, s) => acc + (s.materials || []).length, 0);
                  const contract = p.contract_total || 0;
                  const delivered = p.delivered_qty || 0;
                  const remaining = p.remaining_qty || 0;
                  const percent = contract > 0 ? Math.min((delivered / contract) * 100, 100) : 0;
                  const isCritical = contract > 0 && percent >= 80;
                  const isDone = contract > 0 && remaining <= 0;
                  return (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => openDetail(p)} data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.code || "-"}</TableCell>
                      <TableCell className="font-medium max-w-[200px]"><span className="block truncate">{p.name}</span></TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">{p.client_name || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">{p.sku || "-"}</TableCell>
                      <TableCell className="text-sm">{p.drawing_number || "-"}</TableCell>
                      <TableCell>
                        <Checkbox checked={(p.files || []).length > 0} disabled aria-label="Desenho anexado" data-testid={`product-drawing-attached-${p.id}`} />
                      </TableCell>
                      <TableCell className="text-sm">{p.sale_price ? `R$ ${p.sale_price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "-"}</TableCell>
                      <TableCell className="text-sm">{p.weight ? `${p.weight} kg` : "-"}</TableCell>
                      <TableCell><Badge variant="secondary">{(p.stages || []).length}</Badge></TableCell>
                      <TableCell><Badge variant="outline">{totalMats}</Badge></TableCell>
                      <TableCell data-testid={`product-contract-${p.id}`}>
                        {contract > 0 ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs font-medium">
                              <span className={isDone ? "text-green-700" : isCritical ? "text-red-700" : "text-foreground"}>
                                {delivered.toLocaleString("pt-BR")} / {contract.toLocaleString("pt-BR")}
                              </span>
                              <span className={`text-[10px] font-semibold ${isDone ? "text-green-700" : isCritical ? "text-red-700" : "text-muted-foreground"}`}>
                                {percent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${isDone ? "bg-green-600" : isCritical ? "bg-red-500" : "bg-primary"}`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {isDone ? "Contrato concluido" : `Saldo: ${remaining.toLocaleString("pt-BR")} kits`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sem contrato</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(p)} data-testid={`product-view-${p.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDuplicate(p)} data-testid={`product-duplicate-${p.id}`} title="Duplicar">
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {canDelete && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.id)} data-testid={`product-delete-${p.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Produto</DialogTitle>
            <DialogDescription>Cadastre um produto com suas etapas e materiais padrao</DialogDescription>
          </DialogHeader>
          {renderProductForm(handleCreate, "Cadastrar Produto")}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Produto</DialogTitle>
            <DialogDescription>Altere as informacoes de {selectedProduct?.name}</DialogDescription>
          </DialogHeader>
          {renderProductForm(handleEdit, "Salvar Alteracoes")}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          {selectedProduct && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary" />
                  <div>
                    <DialogTitle className="text-lg">{selectedProduct.name}</DialogTitle>
                    <DialogDescription>
                      {selectedProduct.code && <span className="font-mono">{selectedProduct.code}</span>}
                      {selectedProduct.sku && <span className="ml-2">SKU: {selectedProduct.sku}</span>}
                      {selectedProduct.client_name && <span className="ml-2">| Cliente: {selectedProduct.client_name}</span>}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 max-h-[65vh] overflow-y-auto">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Desenho</span>
                    <p className="mt-1 font-medium">{selectedProduct.drawing_number || "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Preco de Venda</span>
                    <p className="mt-1 font-medium">{selectedProduct.sale_price ? `R$ ${selectedProduct.sale_price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Peso</span>
                    <p className="mt-1 font-medium">{selectedProduct.weight ? `${selectedProduct.weight} kg` : "-"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Altura</span>
                    <p className="mt-1 font-medium">{selectedProduct.height ? `${selectedProduct.height} m` : "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Largura</span>
                    <p className="mt-1 font-medium">{selectedProduct.width ? `${selectedProduct.width} m` : "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Comprimento</span>
                    <p className="mt-1 font-medium">{selectedProduct.length ? `${selectedProduct.length} m` : "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Cubagem</span>
                    <p className="mt-1 font-medium">{selectedProduct.volume ? `${selectedProduct.volume} m3` : "-"}</p>
                  </div>
                </div>

                {/* Stages */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Etapas do Processo ({(selectedProduct.stages || []).length})</h3>
                  <div className="space-y-2">
                    {(selectedProduct.stages || []).map((stage, idx) => (
                      <div key={stage.id || idx} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-muted/30 cursor-pointer" onClick={() => toggleStageExpand(stage.id || idx)}>
                          <span className="text-sm font-medium">{idx + 1}. {stage.name}</span>
                          <div className="flex items-center gap-2">
                            {(stage.sub_stages || []).length > 0 && <Badge variant="secondary" className="text-xs">{stage.sub_stages.length} sub</Badge>}
                            {(stage.materials || []).length > 0 && <Badge variant="outline" className="text-xs">{stage.materials.length} mat</Badge>}
                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedStages[stage.id || idx] ? "rotate-180" : ""}`} />
                          </div>
                        </div>
                        {expandedStages[stage.id || idx] && (
                          <div className="px-3 py-2 space-y-2 text-sm">
                            {(stage.sub_stages || []).length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground font-medium">Sub-etapas:</span>
                                {stage.sub_stages.map((ss, ssIdx) => (
                                  <div key={ssIdx} className="pl-4 py-0.5">{ss.name} - {ss.quantity} {ss.unit}</div>
                                ))}
                              </div>
                            )}
                            {(stage.materials || []).length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground font-medium">Materiais:</span>
                                {stage.materials.map((m, mIdx) => (
                                  <div key={mIdx} className="pl-4 py-0.5">
                                    {m.material_code ? m.material_code + " - " : ""}{m.material_name} - {m.planned_quantity} {m.unit}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {(selectedProduct.stages || []).length === 0 && (
                      <p className="text-sm text-muted-foreground py-2">Nenhuma etapa definida</p>
                    )}
                  </div>
                </div>

                {/* Technical drawings */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold">Desenhos Tecnicos ({(selectedProduct.files || []).length})</h3>
                    {canEdit && (
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" onChange={handleProductUpload} disabled={uploadingFile} data-testid="product-file-upload-input" />
                        <Button variant="outline" size="sm" asChild disabled={uploadingFile}>
                          <span><Upload className="h-3 w-3 mr-1" />{uploadingFile ? "Enviando..." : "Anexar Desenho"}</span>
                        </Button>
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Anexado aqui, o desenho e impresso automaticamente junto com toda ordem deste produto.</p>
                  {(selectedProduct.files || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhum desenho anexado</p>
                  ) : (
                    <div className="space-y-1">
                      {selectedProduct.files.map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-2 rounded border border-border text-sm hover:bg-muted/50 transition-colors" data-testid={`product-file-${f.id}`}>
                          <button type="button" className="flex items-center gap-2 min-w-0 text-left" onClick={() => downloadProductFile(f)} data-testid={`product-file-download-${f.id}`}>
                            <FileText className="h-4 w-4 text-primary shrink-0" />
                            <span className="truncate">{f.original_filename}</span>
                          </button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" onClick={() => handleDeleteProductFile(f.id)} data-testid={`product-file-delete-${f.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {canEdit && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => handleDuplicate(selectedProduct)} data-testid="product-detail-duplicate-btn">
                    <Copy className="h-4 w-4 mr-2" /> Duplicar
                  </Button>
                  <Button variant="outline" onClick={() => openEdit(selectedProduct)} data-testid="product-detail-edit-btn">
                    <Pencil className="h-4 w-4 mr-2" /> Editar
                  </Button>
                  {canDelete && (
                    <Button variant="destructive" onClick={() => handleDelete(selectedProduct.id)} data-testid="product-detail-delete-btn">
                      <Trash2 className="h-4 w-4 mr-2" /> Excluir
                    </Button>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
